import { supabase } from '@/integrations/supabase/client';

const VISITOR_KEY = 'fish_visitor_id';

function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad|tablet|playbook|silk/.test(ua) || (/(android)/.test(ua) && !/mobile/.test(ua))) {
    return 'tablet';
  }
  if (/mobi|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

function getReferrerHost(): string {
  try {
    if (!document.referrer) return '';
    const url = new URL(document.referrer);
    if (url.hostname === location.hostname) return '';
    return url.hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

interface TrackOptions {
  type: 'pageview' | 'contact_click' | 'download' | 'external_link';
  name?: string;
  path?: string;
  metadata?: Record<string, unknown>;
}

export async function track({ type, name, path, metadata }: TrackOptions) {
  try {
    await supabase.from('analytics_events').insert([
      {
        visitor_id: getVisitorId(),
        event_type: type,
        event_name: name ?? null,
        path: path ?? location.pathname,
        referrer_host: getReferrerHost(),
        device_type: getDeviceType(),
        metadata: (metadata ?? null) as never,
      },
    ]);
  } catch {
    // Silently ignore analytics failures
  }
}

export function trackPageview(path: string) {
  void track({ type: 'pageview', path });
}

export function trackContactClick(name: 'phone' | 'email' | 'contact_form') {
  void track({ type: 'contact_click', name });
}

function isDownloadHref(href: string): boolean {
  return (
    /\.(pdf|docx?|xlsx?|pptx?|csv|zip)(\?|$)/i.test(href) ||
    /\/storage\/v1\/object\/public\/(policy-documents|finance-documents|page-images|news-images)\//i.test(href)
  );
}

function getFileLabel(href: string): string {
  try {
    const url = new URL(href, location.origin);
    const last = url.pathname.split('/').pop() || href;
    return decodeURIComponent(last);
  } catch {
    return href;
  }
}

export function installGlobalClickTracker() {
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      if (!href) return;

      // Contact: tel:
      if (href.startsWith('tel:')) {
        void track({ type: 'contact_click', name: 'phone', metadata: { href } });
        return;
      }
      // Contact: mailto:
      if (href.startsWith('mailto:')) {
        void track({ type: 'contact_click', name: 'email', metadata: { href } });
        return;
      }

      // Resolve absolute URL
      let absolute: URL | null = null;
      try {
        absolute = new URL(href, location.origin);
      } catch {
        return;
      }

      const isExternal = absolute.origin !== location.origin;

      // Download (same-origin or external file)
      if (isDownloadHref(absolute.href)) {
        void track({
          type: 'download',
          name: getFileLabel(absolute.href),
          metadata: { href: absolute.href },
        });
        return;
      }

      // External link
      if (isExternal) {
        const host = absolute.hostname.replace(/^www\./, '');
        let name = host;
        if (host.includes('memorygiving')) name = 'memorygiving';
        else if (host.includes('facebook')) name = 'facebook';
        else if (host.includes('justgiving')) name = 'justgiving';
        void track({
          type: 'external_link',
          name,
          metadata: { href: absolute.href, host },
        });
      }
    },
    { capture: true },
  );
}
